import { SECTION_BORDER } from "./constants";

/**
 * Returns thead rows that contain at least one th (skips empty rows).
 */
export function getHeaderRows(table) {
  return Array.from(table.querySelectorAll("thead tr")).filter((tr) =>
    tr.querySelector("th")
  );
}

/**
 * First header row with colspan > 1 when there are 2+ header rows; otherwise null.
 */
export function getTopLevelHeaderRow(headerRows) {
  if (headerRows.length < 2) return null;

  return (
    headerRows.find((tr) =>
      Array.from(tr.querySelectorAll("th")).some((th) => (th.colSpan || 1) > 1)
    ) ?? null
  );
}

/**
 * Top-level sections from a header row: 1-based startCol/endCol per th colspan.
 */
export function getTopLevelSections(topRow) {
  const sections = [];
  let colIndex = 1;

  topRow.querySelectorAll("th").forEach((th) => {
    const colspan = th.colSpan || 1;
    sections.push({
      startCol: colIndex,
      endCol: colIndex + colspan - 1,
    });
    colIndex += colspan;
  });

  return sections;
}

function sectionsToBorderColumns(sections) {
  if (sections.length === 0) return null;

  const left = new Set();
  const right = new Set();

  sections.forEach(({ startCol, endCol }) => {
    left.add(startCol);
    right.add(endCol);
  });

  return { left, right };
}

/**
 * Column indices for left/right borders from the leaf header row (one border per column).
 */
export function getColumnHeaderBorderColumns(table) {
  const headerRows = getHeaderRows(table);
  if (headerRows.length === 0) return null;

  const columnHeaderRow = headerRows[headerRows.length - 1];
  return sectionsToBorderColumns(getTopLevelSections(columnHeaderRow));
}

/**
 * Section borders from top-level colspan headers, or per-column borders from leaf headers.
 */
export function getSectionBorderColumns(table) {
  const headerRows = getHeaderRows(table);
  const topRow = getTopLevelHeaderRow(headerRows);

  if (topRow) {
    return sectionsToBorderColumns(getTopLevelSections(topRow));
  }

  return getColumnHeaderBorderColumns(table);
}

/**
 * Shifts section border column indices when the sheet has a left gutter column.
 */
export function offsetSectionBorderColumns(borderColumns, colOffset) {
  if (!borderColumns || !colOffset) return borderColumns;

  return {
    left: new Set([...borderColumns.left].map((col) => col + colOffset)),
    right: new Set([...borderColumns.right].map((col) => col + colOffset)),
  };
}

/** Removes default Excel gridlines so only explicit cell borders are visible. */
export function hideWorksheetGridLines(worksheet) {
  worksheet.views = [{ showGridLines: false }];
}

/**
 * Clears all cell borders in the used row range (no top/left/bottom/right).
 */
export function clearWorksheetBorders(worksheet, rowStart, rowEnd) {
  for (let rowNumber = rowStart; rowNumber <= rowEnd; rowNumber++) {
    worksheet.getRow(rowNumber).eachCell({ includeEmpty: false }, (cell) => {
      cell.border = {};
    });
  }
}

/**
 * Last 1-based column index that contains exported data.
 */
export function getLastUsedColumn(maxWidthByCol) {
  if (maxWidthByCol.size === 0) return 1;
  return Math.max(...maxWidthByCol.keys());
}

function applyBorderSide(cell, side) {
  cell.border = {
    ...cell.border,
    [side]: SECTION_BORDER,
  };
}

/**
 * Applies thin black top border on the first content row and bottom border on the last row.
 */
export function applyHorizontalBorders(
  worksheet,
  topRow,
  bottomRow,
  colStart,
  colEnd
) {
  for (let colNumber = colStart; colNumber <= colEnd; colNumber++) {
    applyBorderSide(worksheet.getRow(topRow).getCell(colNumber), "top");
    applyBorderSide(worksheet.getRow(bottomRow).getCell(colNumber), "bottom");
  }
}

/**
 * Applies vertical section borders on left/right column edges for all rows in range.
 */
export function applySectionBorders(
  worksheet,
  borderColumns,
  rowStart,
  rowEnd
) {
  const { left, right } = borderColumns;

  for (let rowNumber = rowStart; rowNumber <= rowEnd; rowNumber++) {
    const row = worksheet.getRow(rowNumber);

    left.forEach((colNumber) => {
      applyBorderSide(row.getCell(colNumber), "left");
    });

    right.forEach((colNumber) => {
      applyBorderSide(row.getCell(colNumber), "right");
    });
  }
}

/**
 * Clears borders, draws top/bottom outline, then optional vertical section borders.
 */
export function applyTableBorders(
  worksheet,
  { topRow, bottomRow, colStart, colEnd, sectionBorderColumns }
) {
  hideWorksheetGridLines(worksheet);
  clearWorksheetBorders(worksheet, 1, bottomRow);

  applyHorizontalBorders(worksheet, topRow, bottomRow, colStart, colEnd);

  if (sectionBorderColumns) {
    applySectionBorders(worksheet, sectionBorderColumns, topRow, bottomRow);
  }
}
